import React from "react"
import TableWrapper from "components/TableWrapper"
import {
	Container,
	Box,
	Grid,
	Hidden,
	Table,
	TableHead,
	TableBody,
	TableRow,
	tableRowClasses,
	TableCell,
	TableRowProps,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import SEO from "components/SEO"
import BreadCrumbs from "components/BreadCrumbs"
import { SecureoBreadCrumb } from "@secureo/common/typings/Breadcrumb"
import { CustomAppProps } from "./_app"
import Link from "components/i18n/Link"

import { BsArrowRight } from "react-icons/bs"
import { BsArrowLeft } from "react-icons/bs"

const tableColumns = [
	{
		safetyGrade: "Feuerschutz LFS 30 P nach EN 15659",
		description: "Leichter Feuerschutz für 30 Minuten, für die Lagerung von Papier geeignet",
	},
	{
		safetyGrade: "Feuerschutz UL 72 (USA)	",
		description:
			"Feuerschutznorm aus den USA, häufigste Klassen: 60 Minuten, 90 Minuten und 120 Minuten, jeweils für Papier und Daten",
	},
	{
		safetyGrade: "Feuerschutz NT Fire 017 (Schweden)	",
		description:
			"Feuerschutznorm aus Schweden, häufigste Klassen 60 Minuten, 90 Minuten und 120 Minuten, jeweils für Papier und Daten",
	},
	{
		safetyGrade: "Feuerschutz S 60 P nach EN 1047-1	",
		description:
			"Geprüfter Feuerschutz „Papier“ für 60 Min. Beflammungszeit; max. Temperaturerhöhung im Innenraum um 170°C.",
	},
	{
		safetyGrade: "Feuerschutz S 60 DIS nach EN 1047-1	",
		description:
			"Geprüfter Feuerschutz „Disketten“ für 60 Min. Beflammungszeit; max. Temperaturerhöhung im Innenraum um 50°C, rel. Luftfeuchte max 85%.",
	},
	{
		safetyGrade: "Feuerschutz S 120 DIS nach EN 1047-1	",
		description:
			"Geprüfter Feuerschutz „Disketten“ für 120 Min. Beflammungszeit; max. Temperaturerhöhung im Innenraum um 50°C, rel. Luftfeuchte max 85%.",
	},
	{
		safetyGrade: "DIN 4102	",
		description:
			"Feuerhemmend durch die Verwendung nicht-entflammbarer Füllstoffe, kein Hitzeschutz.",
	},
	{
		safetyGrade: "EN 14470-1 - 90	",
		description:
			"Geprüfter Feuerschutz „Gefahrstoffe“ für 90 Minuten, inklusive selbstschließender Schranktüren und Luftöffnungen für technischen Entlüftungen.",
	},
	{
		safetyGrade: "LFS 60 P nach EN 15659	",
		description:
			"Leichter Schutz vor Feuer, Brandrauch und Löschwasser durch spezielle Füllungen und Dichtungen. 60 Minuten leichter Feuerschutz für Papier",
	},
]

const StyledTableRow = styled(({ ...props }: TableRowProps) => <TableRow {...props} />)(
	({ theme }) => ({
		[`& .${tableRowClasses.root}`]: {
			"&:nth-of-type(odd)": {
				backgroundColor: theme.palette.action.hover,
			},
		},
	}),
)

const asPath = "/tresor-brandschutzklassen"

const breadCrumbAncestors: SecureoBreadCrumb[] = [
	{
		href: "/beratungscenter",
		label: "Infocenter",
	},
]

const currentPath: SecureoBreadCrumb = {
	href: "/tresor-brandschutzklassen",
	label: "Brandschutzklassen",
}

const FireSafetyClassesPage = ({ countryCode }: CustomAppProps) => {
	const title = `Brandschutz: Sicherheitsstufen gegen Feuer // Tresoro.${countryCode.toLowerCase()}`
	const description = `Alle Details zu den Brandschutzklassen hier bei Tresoro.${countryCode.toLowerCase()} ► jetzt Informieren! ✓ Widerstandsklassen ✓ Brandschutz-Richtlinien ✓ uvm.`
	return (
		<>
			<SEO title={title} description={description} asPath={asPath} />
			<BreadCrumbs
				ancestors={breadCrumbAncestors}
				currentPath={currentPath}
				countryCode={countryCode}
			/>
			<Box my={5}>
				<Container>
					<h1 style={{ textAlign: "center" }}>Brandschutzklassen</h1>
					<Grid container spacing={3}>
						<Grid item xs={12} md={7}>
							<p>
								Eine wichtige Überlegung vor dem Kauf eines Tresors ist auch, ob der
								Inhalt nur gegen Einbruch, oder auch gegen Feuer geschützt werden
								soll. Das ist der Fall, wenn beispielsweise wichtige Dokumente darin
								aufbewahrt werden.
							</p>
							<p>
								Wenn Sie einen Brandschutztresor brauchen, können Sie je nach Inhalt
								verschiedene Tresore kaufen. Dabei wird unterschieden zwischen
								Feuerschutz für Dokumente (maximale Innentemperatur nur so hoch,
								dass Papier keinen Schaden nimmt) und Feuerschutz für Daten
								(maximale Innentemperatur übersteigt den für Datenträger kritischen
								Punkt nicht). Brandschutzklassen zeigen also an, wie lange Papiere
								und Datenträger vor Hitze geschützt werden.
							</p>
							<p>
								Die Brandschutzklassen bei Dokumentenschränken reichen beim
								Feuerschutz von 30 bis 120 Minuten. Erkennbar sind diese Tresore am
								„P“ für „Paper“ hinter der Zertifizierung (z.B. LFS30P). Feuerschutz
								für Daten erkennt man am „DIS“ hinter der Zertifizierung (z.B.
								S120DIS). Selbstverständlich sorgen auch bei diesen Modellen
								unabhängige Tests dafür, dass der angegebene Brandschutz auch
								tatsächlich eingehalten wird.
							</p>
						</Grid>
						<Grid item xs={12} md={5} style={{ textAlign: "center", paddingTop: 100 }}>
							<img
								alt="brandschutzklassen"
								src="https://res.cloudinary.com/digitalzylinder-shop/image/upload/v1544692667/Tresoro/Icons/brandschutzklassen.png"
								style={{ height: 150 }}
							/>
						</Grid>
						<Grid item xs={12}>
							<Box
								my={2}
								style={{
									boxShadow: "0 0px 8px rgba(0, 0, 0, 0.1)",
									borderRadius: 5,
								}}
							>
								<TableWrapper>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Sicherheitsstufe</TableCell>
												<TableCell>Beschreibung</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{tableColumns.map((tableColumn, i) => {
												return (
													<StyledTableRow key={i}>
														<TableCell>
															{tableColumn.safetyGrade}
														</TableCell>
														<TableCell>
															{tableColumn.description}
														</TableCell>
													</StyledTableRow>
												)
											})}
										</TableBody>
									</Table>
								</TableWrapper>
							</Box>
							<p>
								Diese Informationen wurden auf Grundlage von VdS und ECB·S-
								Informationen erstellt. Verbindlich sind die VdS-Richtlinien 0691,
								3134-1 und 2333 sowie die ECB·S Empfehlungen Beim Einbau einer
								Einbruchmeldeanlage (EMA) erhöhen sich die Versicherungswerte. Dies
								ist im Einzelfall mit der Versicherungsgesellschaft abzusprechen.
							</p>
							<Box mt={5}>
								<h2 style={{ fontSize: 18 }}>
									Prüfinstanzen für die Sicherheit von Tresoren
								</h2>
								<p>
									Je nach Norm die ein Tresor erfüllt, unterscheiden sich auch die
									Prüfinstanzen. Die Sicherheitsstufen nach den europäischen
									Normen EN 1143-1, EN 1143-2 und EN 14450 können ausschließlich
									von akkreditierten, europäischen Zertifizierungsstellen
									bescheinigt werden. Dabei steht es jedem EU-Mitgliedsstaat frei,
									welche Institute diese nationale Akkreditierung erhalten. In
									Deutschland gibt es mit ECB-S (European Certification Body) und
									VdS (VdS Schadenverhütung GmbH) zwei dieser anerkannten
									Zertifizierungsstellen. In Österreich gibt es als Pendant den
									VSÖ (Verband der Sicherheitsunternehmen Österreichisches).
									Weitere anerkannte europäsche Prüfstellen sind unter anderem IMP
									(Polen) oder Trezor Test (Tschechien). Sicherheitsstufen nach
									VDMA 24992 (A, B) sind überholte Einstufungen, die nicht mehr
									neu ausgestellt werden.
								</p>
								<Box
									my={4}
									className="vertical-align-border"
									style={{
										width: "100%",
										marginLeft: "auto",
										marginRight: "auto",
									}}
								/>
							</Box>
							<Box
								my={5}
								style={{ display: "flex", justifyContent: "space-between" }}
							>
								<Link
									style={{ color: "inherit" }}
									href="/beratung-einbruch-und-feuersichere-tresore"
								>
									<div style={{ display: "flex", alignItems: "center" }}>
										<Hidden smDown>
											<img
												alt="infocenter"
												width="75px"
												src="https://res.cloudinary.com/digitalzylinder-shop/image/upload/c_fill,dpr_auto,f_auto,q_auto/v1544692669/Tresoro/Icons/feuersicher.png"
											/>
										</Hidden>
										<BsArrowLeft size="1.3rem" style={{ marginRight: 10 }} />
										Feuersichere Tresore
									</div>
								</Link>
								<Link style={{ color: "inherit" }} href="/versicherungssummen">
									<div style={{ display: "flex", alignItems: "center" }}>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												textAlign: "right",
											}}
										>
											Zur den Versicherungssummen
											<BsArrowRight
												size="1.3rem"
												style={{ marginLeft: 10 }}
											/>
										</div>
										<Hidden smDown>
											<img
												alt="infocenter"
												width="75px"
												src="https://res.cloudinary.com/digitalzylinder-shop/image/upload/c_fill,dpr_auto,f_auto,q_auto/v1543562107/Tresoro/Icons/versicherungssummen-icon_2.png"
											/>
										</Hidden>
									</div>
								</Link>
							</Box>
						</Grid>
					</Grid>
				</Container>
			</Box>
		</>
	)
}

export default FireSafetyClassesPage
